import React, { useState, useEffect, useRef } from 'react';
import useTelaMobile from '../../../utils/useTelaMobile';
import { AlertCircle } from 'lucide-react';
import { injetarShimStorage, lerMemoriaDoGuia, gravarMemoriaDoGuia } from './chamados-guia-storage';
import { injetarTemaDoModulo } from './chamados-guia-tema';

// =====================================================================
// Chamados — o GPS-TI **com a equipe** (guia interno).
//
// O GPS-TI é o guia de pesquisa de suporte do TI, feito pelo Sebastião.
//
// ⚠️⚠️ ESTA É A ÚNICA CÓPIA DESDE 27/08/2026. O guia nasceu naquele dia em duas:
//    uma PÚBLICA em `/gps-ti/` (sem login e SEM a equipe, linkada no Passo 1 da
//    abertura) e esta INTERNA, com o CELULAR e a ESCALA dos técnicos. A pública
//    foi desligada horas depois, a pedido da Julia — "deixe só dentro do site" —,
//    e o Passo 1 passou a apontar para cá.
//
// ⚠️⚠️ CONSEQUÊNCIA QUE NÃO SE VÊ OLHANDO ESTA TELA: a loja deixou de ter uma
//    versão higienizada. `Gerente` e `Gerente Plantao` já alcançavam esta aba
//    (`PERMISSIONS.manual`), mas por um caminho secundário; agora ela é o
//    caminho principal, e o celular dos técnicos passa a ser lido pela rede
//    inteira. Foi decisão consciente — o mesmo comentário do `PERMISSIONS`
//    argumenta que quem está com o equipamento quebrado na mão precisa saber
//    quem está de plantão — mas quem for mexer aqui precisa saber que essa
//    exposição é o preço, não um descuido.
//
// ⚠️⚠️ POR QUE UM IFRAME ISOLADO, E NÃO UMA ABA NOVA. São 800 KB de app com
//    `<script>` próprio, que não passa por revisão a cada revisão do Sebastião.
//    Aberto numa aba da NOSSA origem, esse script conseguiria ler o `authToken`
//    do `localStorage` e sair com a sessão de quem o abriu. Dentro de um
//    `sandbox` SEM `allow-same-origin`, ele roda em ORIGEM OPACA: enxerga o
//    próprio conteúdo e mais nada.
//    ⚠️ É o mesmo desenho do módulo Projetos, pela mesma razão — e a razão de o
//    backend devolver o HTML como STRING EM JSON, nunca como `text/html`.
//    ⚠️ NUNCA acrescentar `allow-same-origin` aqui. Some a proteção inteira.
//
// ⚠️ O que se perde na caixa isolada — e que a cópia pública dava, por ser um
//    arquivo estático servido pelo Nginx: o guia não se instala mais como app no
//    tablet, não funciona sem rede, e não guarda a sigla da loja entre visitas
//    (os 4 usos de `localStorage` dele têm `try/catch`; o service worker se
//    auto-desliga). Nada disso quebra a LEITURA, mas some o uso em loja com
//    internet instável, que era um dos motivos de o guia existir.
// =====================================================================

const API = '/api/chamados';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// ⚠️ Só a clara sobrou: o roxo cheio (#6b21a8) era usado no rodapé, que saiu em
//    28/08. A cor do módulo continua sendo ele — ver o hub `Chamados.js`.
const COR_CLARA = 'var(--ch-cor-clara)';

const ChamadosManual = ({ onAbrirChamado }) => {
  // ⚠️ O guia é lido NA LOJA, no aparelho — foi para o celular que ele nasceu.
  const { ehMobile } = useTelaMobile();

  const [html, setHtml] = useState('');
  const [geradoEm, setGeradoEm] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  // O de-para ficha → família, que vem JUNTO com o guia na mesma resposta.
  const [fichas, setFichas] = useState({});
  // A última gravação feita nesta sessão — só para a tela poder confirmar por
  // fora do guia. ⚠️ O aviso que importa é o DE DENTRO do guia (o rodapé da
  // barra de edição); este é reforço, porque quem salva está olhando para lá.
  const [salvo, setSalvo] = useState(null);
  const iframeRef = useRef(null);

  // ---------------------------------------------------------------------
  // A PONTE COM O GUIA (2026-08-28) — postMessage, e não link.
  // ---------------------------------------------------------------------
  // O botão "Abrir chamado no portal" que fecha cada ficha era um
  // `<a target="_blank">`: abria OUTRA aba do navegador, perdendo o contexto e
  // obrigando a pessoa a achar o equipamento numa lista de ~170. Agora o guia
  // avisa o portal por `postMessage`, e a troca de aba acontece aqui dentro,
  // levando junto a ficha que estava aberta.
  //
  // ⚠️⚠️ `event.origin` É INÚTIL AQUI, e quem for endurecer isto precisa saber:
  //    o iframe roda em `sandbox` SEM `allow-same-origin`, ou seja, em ORIGEM
  //    OPACA — o `origin` da mensagem chega como a string "null", não como o
  //    nosso domínio. Comparar com `window.location.origin` recusaria TODAS as
  //    mensagens legítimas; aceitar `"null"` aceitaria qualquer iframe opaco da
  //    página. Por isso a identidade é conferida por `event.source`, que é a
  //    janela do NOSSO iframe e não se falsifica.
  //
  // ⚠️ E o conteúdo é validado à parte: a ficha tem de casar a forma do guia
  //    (`G-01`, `K-07`…) e existir no de-para. Nada do payload vira consulta,
  //    caminho de arquivo ou HTML — é uma chave de dicionário, e só.
  useEffect(() => {
    const aoReceber = (ev) => {
      // (a) veio do NOSSO iframe? (a única prova de identidade que sobra)
      if (!iframeRef.current || ev.source !== iframeRef.current.contentWindow) return;
      const d = ev.data;

      // ---------------------------------------------------------------
      // O guia pedindo para LEMBRAR de algo (2026-08-28).
      // ---------------------------------------------------------------
      // ⚠️ É o outro lado do shim de `localStorage` injetado no `srcDoc`: o
      //    guia roda em origem opaca e não tem storage, então quem guarda é o
      //    portal. Sem isto, a tela de abertura ("Entendido") reaparece a CADA
      //    visita e a sigla da loja é perguntada de novo toda vez.
      // ⚠️ `gravarMemoriaDoGuia` recusa chave que não seja do guia e valor
      //    grande demais — o guia é script não auditado, e a lista fechada é o
      //    que impede o `localStorage` do portal de virar depósito dele.
      if (d && d.tipo === 'gps-ti:lembrar') {
        gravarMemoriaDoGuia(window.localStorage, d.chave, d.valor);
        return;
      }

      // ---------------------------------------------------------------
      // O guia pedindo para SALVAR uma revisão (2026-09-14).
      // ---------------------------------------------------------------
      // ⚠️⚠️ ISTO SUBSTITUI O BOTÃO "BAIXAR ARQUIVO", que aqui dentro NUNCA
      //    funcionou: o `sandbox` não tem `allow-downloads`, então o Chrome
      //    barrava o download EM SILÊNCIO — sem erro no console, sem aviso na
      //    tela. O editor do guia parecia funcionar e não gravava nada (foi
      //    assim que a ficha K-12 se perdeu, em 14/09).
      //    ⚠️ E a flag não pode simplesmente entrar: quem protege o `authToken`
      //    aqui é a AUSÊNCIA de `allow-same-origin`, que mantém a origem opaca.
      //
      // ⚠️ A RESPOSTA É OBRIGATÓRIA, nos dois desfechos. O guia só zera o
      //    contador de alterações pendentes quando recebe `ok: true`; ficar
      //    calado num erro faria a tela dele anunciar "salvo" sobre uma gravação
      //    que não aconteceu — exatamente o bug que isto conserta.
      if (d && d.tipo === 'gps-ti:salvar') {
        const responder = (carga) => {
          // ⚠️ `ev.source` e não `iframeRef.current.contentWindow`: é a mesma
          //    janela, mas usar a que MANDOU a mensagem evita responder ao
          //    iframe errado se um dia houver mais de um nesta tela.
          try { ev.source.postMessage({ tipo: 'gps-ti:salvo', ...carga }, '*'); } catch (e) { /* aba fechada */ }
        };
        if (typeof d.html !== 'string' || !d.html.trim()) {
          responder({ ok: false, erro: 'o conteúdo não chegou' });
          return;
        }
        fetch(`${API}/gps-ti`, {
          method: 'POST',
          headers: { ...auth(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ html: d.html }),
        })
          .then(async (r) => {
            const resp = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(resp.message || `não deu para salvar (HTTP ${r.status})`);
            return resp;
          })
          .then((resp) => {
            setSalvo({ quando: new Date(), rev: resp.rev });
            responder({ ok: true });
          })
          .catch((e) => responder({ ok: false, erro: e.message }));
        return;
      }

      if (!d || d.tipo !== 'gps-ti:abrir-chamado') return;

      // (b) a ficha tem a forma do guia? Qualquer outra coisa vira `null`, que
      //     significa "sem filtro" — nunca um erro na cara da pessoa.
      const ficha = typeof d.ficha === 'string' && /^[GKPS]-\d{2}$/.test(d.ficha.trim().toUpperCase())
        ? d.ficha.trim().toUpperCase()
        : null;

      // ⚠️ `fichas[ficha]` indefinido é o caminho NORMAL, não exceção: ficha nova
      //    (o guia tem editor embutido), ficha de problema da loja inteira ou
      //    ficha ambígua não têm família. `undefined` → `null` → mostra tudo.
      if (onAbrirChamado) onAbrirChamado(ficha ? (fichas[ficha] || null) : null);
    };

    window.addEventListener('message', aoReceber);
    return () => window.removeEventListener('message', aoReceber);
  }, [fichas, onAbrirChamado]);

  useEffect(() => {
    // ⚠️ 800 KB numa requisição só, e sem cache do navegador (é POST-like: rota
    //    autenticada, resposta JSON). É o preço de não deixar o arquivo público.
    //    No celular demora — daí o aviso de "carregando" dizer o tamanho.
    fetch(`${API}/gps-ti-interno`, { headers: auth() })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.message || `O guia não carregou (HTTP ${r.status}).`);
        return d;
      })
      // ⚠️ O de-para vem na MESMA resposta, de propósito: uma requisição só, sem
      //    rota nova, e — o que importa — sem uma CÓPIA do mapa no front, que
      //    divergiria do backend no primeiro ajuste. O mapa viaja junto do guia
      //    que ele descreve.
      .then((d) => { setHtml(d.html || ''); setGeradoEm(d.geradoEm || null); setFichas(d.fichas || {}); })
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');

  // O guia, com a memória que o portal guardou por ele e na COR DO MÓDULO.
  // ⚠️ `useMemo` não é otimização aqui: são 800 KB de string, e refazer a
  //    injeção a cada render trocaria o `srcDoc` do iframe — o que RECARREGA o
  //    guia inteiro e joga fora a ficha que a pessoa estava lendo.
  //
  // ⚠️⚠️ A ORDEM DAS DUAS INJEÇÕES NÃO IMPORTA, mas o LUGAR de cada uma sim, e
  //    elas são opostas de propósito: o shim de storage entra logo DEPOIS do
  //    `<head>` (precisa estar instalado antes de o guia ler o storage) e o tema
  //    entra ANTES do `</head>` (precisa vir depois do `<style>` do guia para
  //    vencer a redeclaração do `:root`). Ver os dois módulos ao lado.
  const htmlPronto = React.useMemo(
    () => (html ? injetarTemaDoModulo(injetarShimStorage(html, lerMemoriaDoGuia(window.localStorage))) : ''),
    [html],
  );

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
        Manual do TI — com o plantão
      </h2>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px 0' }}>
        O mesmo GPS-TI da loja, com o <strong>contato e a escala dos técnicos</strong>.
        {geradoEm && <> · cópia gerada em {fmt(geradoEm)}</>}
      </p>

      {/* ⚠️ Confirmação VISÍVEL da gravação, e ela tem um motivo específico: o
          bug que este caminho conserta era justamente um "salvar" que não
          salvava sem dizer nada. Um salvamento silencioso — ainda que correto —
          deixaria a mesma dúvida que causou a perda da ficha K-12. */}
      {salvo && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px', marginBottom: 16,
        }}>
          <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>
            <strong>Revisão salva no portal</strong>
            {salvo.rev ? <> (revisão {salvo.rev})</> : null} às{' '}
            {salvo.quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}.
            {' '}Toda a rede passa a ver esta versão.
          </p>
        </div>
      )}

      {/* ⚠️⚠️ O AVISO DE USO INTERNO SAIU EM 2026-08-28, a pedido da Julia, junto
          com o rodapé — para dar ao guia a altura da tela.
          ⚠️ O QUE ELE DIZIA CONTINUA VERDADE, e quem mexer aqui precisa saber:
          esta tela mostra o CELULAR PESSOAL e a ESCALA de folga/férias dos três
          técnicos, e desde 27/08 ela é a ÚNICA versão do guia — `Gerente` e
          `Gerente Plantao` leem exatamente esta. A exposição é conhecida e
          aceita (está registrada no cabeçalho deste arquivo e no CLAUDE.md); o
          que se perdeu foi o LEMBRETE na tela de que um print daqui carrega
          dado pessoal. Não é motivo para repor o bloco sem pedido — é motivo
          para não repor os dados em nenhum lugar mais aberto que este. */}

      {erro && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start', backgroundColor: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 16,
        }}>
          <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ margin: 0, fontSize: 13, color: '#991b1b' }}>{erro}</p>
        </div>
      )}

      {carregando && (
        <p style={{ fontSize: 13, color: '#64748b' }}>
          Carregando o guia… são cerca de 800 KB (as fotos vêm dentro do arquivo).
        </p>
      )}

      {!!html && (
        /* ⚠️⚠️ `sandbox` SEM `allow-same-origin` — é a linha inteira da proteção.
              • allow-scripts ....... o guia É um app; sem isso não abre ficha nenhuma
              • allow-popups ........ os links de WhatsApp usam target="_blank"
              • allow-popups-to-escape-sandbox .. para o WhatsApp abrir fora da caixa
              • allow-forms ......... a busca e o campo de loja do guia
           ⚠️ NÃO acrescentar `allow-same-origin`: com ele o script do guia passa a
              enxergar o `localStorage` do portal, e o `authToken` junto. */
        <iframe
          ref={iframeRef}
          title="GPS-TI — guia interno do TI"
          /* ⚠️⚠️ O HTML VAI COM UM SHIM DE `localStorage` INJETADO (2026-08-28).
             Em origem opaca o storage do iframe não existe — acessá-lo lança —,
             então o guia esquecia tudo a cada visita: a tela de abertura voltava
             sempre (o sintoma que a Julia relatou) e a sigla da loja era
             perguntada de novo. O shim atende o guia em memória e manda o portal
             guardar; na visita seguinte a semente já vem pronta.
             ⚠️ A semente leva SÓ as chaves do guia — nunca o `localStorage` do
             portal, onde mora o `authToken`. Ver a trava em
             `chamados-guia-storage.js`, com teste nomeado. */
          srcDoc={htmlPronto}
          /* ⚠️⚠️ `allow-modals` ENTROU EM 2026-08-28, e sem ele METADE DO GUIA NÃO
             FUNCIONA DENTRO DO PORTAL. Sintoma relatado pela Julia: "clico no
             botão ENTENDIDO da pop inicial e não fecha".
             O guia tem SEIS `<dialog>` — intro, plantão do técnico, lupa das
             fotos, senha, editor e equipe — e todos abrem por `showModal()`
             (`abrirPainel`, no guia). A especificação HTML exige `allow-modals`
             justamente para `showModal()`: sem a flag, o navegador bloqueia a
             chamada, e o painel não abre ou não responde.
             ⚠️⚠️ O CASO GRAVE NÃO É A INTRO, É O PAINEL DO TÉCNICO (`#tec`): é
             ele que mostra quem está de plantão e o contato. Ou seja, isto é a
             SEGUNDA causa do "não consigo falar com o técnico" — a primeira foi
             o botão ☎ que a transformação de 27/08 apagou das fichas. Repor o
             botão sem esta flag daria um botão que abre nada.
             ⚠️ `allow-modals` NÃO enfraquece a proteção que importa: ele libera
             `alert`/`confirm`/`prompt`/`showModal`, e NADA disso dá ao guia
             acesso à nossa origem nem ao `authToken`. Quem guarda isso é a
             AUSÊNCIA de `allow-same-origin`, que continua. */
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-modals"
          /* ⚠️ 88vh desde 28/08 (era 78vh), quando o aviso e o rodapé saíram: o
             guia é a tela inteira agora, e quem o usa está de pé na loja
             procurando um passo — cada linha a mais evita uma rolagem.
             ⚠️⚠️ NO CELULAR É `100dvh` MENOS o cabeçalho, e a unidade importa:
             `vh` no celular é medido com a barra de endereço RETRAÍDA, então
             88vh é mais alto que a área realmente visível — o fim do guia (e o
             que estiver perto dele) fica embaixo da barra do navegador, fora do
             alcance do dedo. `dvh` acompanha a barra aparecendo e sumindo.
             ⚠️ O guia JÁ é responsivo por conta própria (tem `viewport` meta,
             `@media (max-width:640px)` e `@media (pointer:coarse)`) — o que
             faltava era a caixa dar a ele uma altura honesta. */
          style={{
            width: '100%',
            height: ehMobile ? 'calc(100dvh - 190px)' : '88vh',
            minHeight: ehMobile ? 420 : undefined,
            border: `1px solid ${COR_CLARA}`,
            borderRadius: 10, backgroundColor: '#fff',
            /* ⚠️ Sem isto o iOS ignora a altura acima e estica o iframe até o
               tamanho do conteúdo — o guia inteiro vira uma página gigante, o
               scroll interno some e rolar até uma ficha fica impraticável. */
            display: 'block',
          }}
        />
      )}

      {/* ⚠️ O RODAPÉ SAIU EM 2026-08-28 (Julia), para o guia ganhar altura.
          Ele dizia duas coisas que continuam verdade e agora só vivem no código:
          • aqui o guia NÃO se instala como app e NÃO guarda a sigla da loja entre
            visitas — o `sandbox` do iframe desliga o service worker. E a "versão
            pública" que o texto oferecia como alternativa NÃO EXISTE desde
            27/08, então o rodapé já estava meio errado: mandava a loja para um
            endereço desligado.
          • o conteúdo é do Sebastião; revisão nova entra por
            `server/chamados/gps-ti-gerar.js`, nunca editando o arquivo do repo.
          Detalhe em `server/chamados/README-gps-ti.md`. */}
    </div>
  );
};

export default ChamadosManual;
